import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { LocalService, Errors, CashpaymentService } from '../../../../core';
import { FileService } from '../../../../core';
import { DataTableColumn } from '../../../../shared/ui/data-table/data-table.component';

@Component({
  selector: 'app-cash-payment',
  templateUrl: './cash-payment.component.html',
  styleUrls: ['./cash-payment.component.scss']
})
export class CashPaymentComponent implements OnInit, AfterViewInit {
  @ViewChild('statusColumnTemplate') statusColumnTemplate!: TemplateRef<any>;
  @ViewChild('popStatusColumnTemplate') popStatusColumnTemplate!: TemplateRef<any>;
  @ViewChild('actionsColumnTemplate') actionsColumnTemplate!: TemplateRef<any>;

  userProfile: any;
  errors: Errors = { errors: {} };

  // Filter state (owned here, passed to cash-payment-filters child)
  partnerLevelStatusList: any[] = [];
  statusDropdownItems: any[] = [];
  fiscalYearDropdownItems: any[] = [];
  quarterDropdownItems: any[] = [
    { id: '', name: 'All' },
    { id: 'Q1', name: 'Q1' },
    { id: 'Q2', name: 'Q2' },
    { id: 'Q3', name: 'Q3' },
    { id: 'Q4', name: 'Q4' }
  ];
  partnerStatus: any = '';
  partnerFiscalYear: any = '';
  partnerFiscalQtr: any = '';

  // Main table
  expandedTableColumns: DataTableColumn[] = [
    { field: 'sourceSystemTransactionId', header: 'Transaction Id' },
    { field: 'claimQuarter', header: 'Claim Quarter' },
    { field: 'amount', header: 'Amount' }
  ];
  partnerLevelTableColumns: DataTableColumn[] = [];
  partnerlevlPageNo: number = 1;
  partnerLevelSize: number = 3;
  partnerLevelGridData: any = [];
  partnerLevelTableRows: any[] = [];

  // Inline popover breakdown
  expandedTableRows: any[] = [];
  expandedItemId: any = null;
  isExpandedDataLoading: boolean = false;
  breakdownPageNo: number = 1;
  breakdownPageSize: number = 5;
  selectedBreakDownRunCashpaymentId: any;
  partnerBreakdownRunList: any = [];

  // Partner breakdown full section
  showPartnerLevwlBreakDownSection: boolean = false;
  selectedPartnerData: any;

  // Status history modal state
  statusHistoryModalOpen: boolean = false;
  selectedStatusHistoryCashPaymentId: any;

  // Confirm payment modal state
  confirmPaymentModalOpen: boolean = false;
  selectedConfirmPaymentCashPaymentId: any;

  constructor(
    private cashPaymentService: CashpaymentService,
    private fileService: FileService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userProfile = LocalService.get('userProfile');
    this.generateFiscalYearOptions();
    this.getPartnerLevelStatus();
    this.getPartnerConsolidatedList();
  }

  ngAfterViewInit(): void {
    this.partnerLevelTableColumns = [
      { field: 'fiscalPeriod', header: 'Fiscal period', align: 'center' },
      { field: 'amount', header: 'Amount', isBold: true, align: 'center' },
      { field: 'status', header: 'Status', align: 'center', cellTemplate: this.statusColumnTemplate },
      { field: 'popStatus', header: 'PoP Status', align: 'center', cellTemplate: this.popStatusColumnTemplate },
      { field: 'actions', header: '', align: 'center', cellTemplate: this.actionsColumnTemplate }
    ];
    this.cdr.markForCheck();
  }

  generateFiscalYearOptions(): void {
    const currentYear = new Date().getFullYear();
    const years: any[] = [{ id: '', name: 'All' }];
    for (let i = -2; i <= 1; i++) {
      const year = currentYear + i;
      years.push({ id: year.toString(), name: 'FY' + year });
    }
    this.fiscalYearDropdownItems = years;
  }

  getPartnerLevelStatus(): void {
    this.cashPaymentService.getPartnerLevelStatusList().subscribe(
      (data: any) => {
        this.partnerLevelStatusList = data;
        this.statusDropdownItems = [{ id: '', name: 'All' }, ...data];
      },
      (err: any) => {
        this.errors = err;
      }
    );
  }

  onFilterStatusChange(event: any): void {
    this.partnerStatus = event.id;
  }

  onFilterFiscalYearChange(event: any): void {
    this.partnerFiscalYear = event.id;
  }

  onFilterQuarterChange(event: any): void {
    this.partnerFiscalQtr = event.id;
  }

  onFilterSearch(): void {
    this.getPartnerConsolidatedList('reset');
  }

  onFilterReset(): void {
    this.partnerStatus = '';
    this.partnerFiscalYear = '';
    this.partnerFiscalQtr = '';
    this.getPartnerConsolidatedList('reset');
  }

  getPartnerConsolidatedList(pageIndex?: any, pageEvent?: any): void {
    let req = '';

    if (pageIndex === 'reset') {
      this.partnerlevlPageNo = 1;
      this.partnerLevelSize = 3;
    }
    if (pageEvent) {
      this.partnerlevlPageNo = pageEvent;
    }

    if (this.partnerStatus) {
      req = req + '&status=' + this.partnerStatus;
    }
    if (this.partnerFiscalYear) {
      req = req + '&fiscalYear=' + this.partnerFiscalYear;
    }
    if (this.partnerFiscalQtr) {
      req = req + '&quarter=' + this.partnerFiscalQtr;
    }

    req = 'companyId=' + this.userProfile.company.id + req +
      '&page=' + (this.partnerlevlPageNo - 1) +
      '&size=' + this.partnerLevelSize;

    this.cashPaymentService.getListOfPartnerConsolidatedData(req).subscribe(
      (data: any) => {
        this.partnerLevelGridData = data;
        this.partnerLevelTableRows = this.buildPartnerLevelRows(data);
      },
      (err: any) => {
        this.errors = err;
      }
    );
  }

  private buildPartnerLevelRows(data: any): any[] {
    if (!data || !data.content) {
      return [];
    }
    return data.content.map((item: any) => ({
      ...item,
      fiscalPeriod: 'FY' + item.fiscalYear + ' ' + item.quarter,
      amount: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(item.amount || 0)
    }));
  }

  // Inline popover breakdown
  onPopoverVisibleChange(isVisible: boolean, item: any, event?: any): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (isVisible) {
      this.expandedItemId = item.cashPaymentId;
      this.selectedBreakDownRunCashpaymentId = item.cashPaymentId;
      this.breakdownPageNo = 1;
      this.isExpandedDataLoading = true;
      this.getpartnerBreakdownRunData();
    } else {
      this.expandedItemId = null;
    }
  }

  getpartnerBreakdownRunData(): void {
    const req = 'companyId=' + this.userProfile.company.id +
      '&cashPaymentId=' + this.selectedBreakDownRunCashpaymentId +
      '&page=' + (this.breakdownPageNo - 1) +
      '&size=' + this.breakdownPageSize;

    this.cashPaymentService.getPartnerConsolidatedBreakdownRunData(req).subscribe(
      (data: any) => {
        this.partnerBreakdownRunList = data;
        this.expandedTableRows = this.buildExpandedRows(data);
        this.isExpandedDataLoading = false;
      },
      (err: any) => {
        this.errors = err;
        this.isExpandedDataLoading = false;
      }
    );
  }

  onBreakdownPageChange(page: number): void {
    this.breakdownPageNo = page;
    this.isExpandedDataLoading = true;
    this.getpartnerBreakdownRunData();
  }

  private buildExpandedRows(data: any): any[] {
    if (!data || !data.content) {
      return [];
    }
    return data.content.map((item: any) => ({
      ...item,
      claimQuarter: 'FY' + item.dealFiscalYear + ' ' + item.dealQuarter,
      amount: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(item.amount || 0)
    }));
  }

  // Full partner breakdown section
  onActionButtonClick(action: string, item: any, event: any): void {
    event.stopPropagation();
    event.preventDefault();
    this.showPartnerBreakdownSec(action, item);
  }

  showPartnerBreakdownSec(action: string, item?: any): void {
    if (action === 'show') {
      this.showPartnerLevwlBreakDownSection = true;
      this.selectedPartnerData = item;
    } else {
      this.showPartnerLevwlBreakDownSection = false;
    }
  }

  // Status history modal
  showStatusHistoryPopup(row: any): void {
    this.selectedStatusHistoryCashPaymentId = row.cashPaymentId;
    this.statusHistoryModalOpen = true;
  }

  onStatusHistoryClose(): void {
    this.statusHistoryModalOpen = false;
    this.selectedStatusHistoryCashPaymentId = null;
  }

  // Confirm payment modal
  showConfirmPaymentPopup(item: any): void {
    this.selectedConfirmPaymentCashPaymentId = item.cashPaymentId;
    this.confirmPaymentModalOpen = true;
  }

  onConfirmPaymentClose(): void {
    this.confirmPaymentModalOpen = false;
    this.selectedConfirmPaymentCashPaymentId = null;
  }

  onPaymentConfirmed(): void {
    this.confirmPaymentModalOpen = false;
    this.selectedConfirmPaymentCashPaymentId = null;
    this.getPartnerConsolidatedList();
  }

  // Export
  onFilterExport(): void {
    const url = this.cashPaymentService.buildPartnerlevelExportUrl(this.userProfile.company.id);
    const fileName = 'Payment_File.xlsx';
    this.fileService.excelDownloadFile(url, fileName, true);
  }

  partnerLevelBreakdownExportData(id?: any): void {
    const cashPaymentId = id || (this.selectedPartnerData && this.selectedPartnerData.cashPaymentId);
    const url = this.cashPaymentService.buildPartnerLevelBreakdownExportUrl(this.userProfile.company.id, cashPaymentId);
    const fileName = 'Payment_File.xlsx';
    this.fileService.excelDownloadFile(url, fileName, true);
  }
}
